import React, { ChangeEvent, useState } from 'react';
import {
  SelectChangeEvent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  TextField,
  Grid,
  Box,
  Chip,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Collapse,
  CircularProgress,
  Link,
  Typography,
  Divider,
} from '../../ui';
import { CheckCircle2 as CheckCircleIcon, Download as SystemUpdateIcon, ArrowRight as ArrowForwardIcon } from 'lucide-react';
import { YtDlpVersionInfo, YtDlpUpdateStatus } from '../hooks/useYtDlpUpdate';
import { ConfigurationCard } from '../common/ConfigurationCard';
import { InfoTooltip } from '../common/InfoTooltip';
import SubtitleLanguageSelector from '../SubtitleLanguageSelector';
import { VideoFilenameTemplate } from './components/VideoFilenameTemplate';
import { SubfolderAutocomplete } from '../../shared/SubfolderAutocomplete';
import { ManageSubfoldersDialog } from '../../shared/ManageSubfoldersDialog';
import { useSubfolders } from '../../../hooks/useSubfolders';
import { ConfigState, DeploymentEnvironment, PlatformManagedState } from '../types';
import { reverseFrequencyMapping, getChannelFilesOptions } from '../helpers';
import { FREQUENCY_MAPPING } from '../constants';
import { formatDateTime } from '../../../utils/formatters';

interface CoreSettingsSectionProps {
  config: ConfigState;
  deploymentEnvironment: DeploymentEnvironment;
  isPlatformManaged: PlatformManagedState;
  onConfigChange: (updates: Partial<ConfigState>) => void;
  onMobileTooltipClick?: (text: string) => void;
  token: string | null;
  filenameTemplateSaveRequirement?: string | null;
  onFilenameTemplatePreviewSuccess?: (prefix: string) => void;
  ytDlpVersionInfo?: YtDlpVersionInfo;
  ytDlpUpdateStatus?: YtDlpUpdateStatus;
  onYtDlpUpdate?: () => void;
}

export const CoreSettingsSection: React.FC<CoreSettingsSectionProps> = ({
  config,
  deploymentEnvironment,
  isPlatformManaged,
  onConfigChange,
  onMobileTooltipClick,
  token,
  filenameTemplateSaveRequirement,
  onFilenameTemplatePreviewSuccess,
  ytDlpVersionInfo,
  ytDlpUpdateStatus,
  onYtDlpUpdate,
}) => {
  // Fetch available subfolders
  const { subfolders, loading: subfoldersLoading, createSubfolder } = useSubfolders(token);

  // State for confirmation dialog when setting default subfolder
  const [manageOpen, setManageOpen] = useState(false);
  const [pendingDefaultSubfolder, setPendingDefaultSubfolder] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [affectedChannels, setAffectedChannels] = useState<{ count: number; channelNames: string[] }>({ count: 0, channelNames: [] });
  const [loadingAffectedChannels, setLoadingAffectedChannels] = useState(false);
  const [showAffectedList, setShowAffectedList] = useState(false);
  const [showYtDlpUpdateDialog, setShowYtDlpUpdateDialog] = useState(false);

  // Handle default subfolder change with confirmation
  const handleDefaultSubfolderChange = async (newValue: string | null) => {
    const currentValue = config.defaultSubfolder || '';
    const newValueNormalized = newValue || '';

    // No change
    if (currentValue === newValueNormalized) {
      return;
    }

    // Show dialog immediately with loading state
    setPendingDefaultSubfolder(newValue);
    setShowConfirmDialog(true);
    setLoadingAffectedChannels(true);
    setAffectedChannels({ count: 0, channelNames: [] });

    // Fetch affected channels count
    try {
      const response = await fetch('/api/channels/using-default-subfolder', {
        headers: { 'x-access-token': token || '' },
      });
      if (response.ok) {
        const data = await response.json();
        setAffectedChannels(data);
      }
    } catch (err) {
      console.error('Failed to fetch affected channels:', err);
      setAffectedChannels({ count: 0, channelNames: [] });
    } finally {
      setLoadingAffectedChannels(false);
    }
  };

  const handleConfirmDefaultSubfolder = () => {
    onConfigChange({ defaultSubfolder: pendingDefaultSubfolder || '' });
    setShowConfirmDialog(false);
    setPendingDefaultSubfolder(null);
    setShowAffectedList(false);
  };

  const handleCancelDefaultSubfolder = () => {
    setShowConfirmDialog(false);
    setPendingDefaultSubfolder(null);
    setShowAffectedList(false);
  };

  const [pendingFlatDefault, setPendingFlatDefault] = useState<boolean | null>(null);
  const [showFlatConfirmDialog, setShowFlatConfirmDialog] = useState(false);
  const [flatAffectedChannels, setFlatAffectedChannels] = useState<{ count: number; channelNames: string[] } | null>(null);
  const [loadingFlatAffectedChannels, setLoadingFlatAffectedChannels] = useState(false);
  const [showFlatAffectedList, setShowFlatAffectedList] = useState(false);

  const handleFlatDefaultChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    if (newValue === config.defaultSkipVideoFolder) {
      return;
    }

    setPendingFlatDefault(newValue);
    setShowFlatConfirmDialog(true);
    setLoadingFlatAffectedChannels(true);
    setFlatAffectedChannels(null);

    try {
      const response = await fetch('/api/channels/using-global-file-structure', {
        headers: { 'x-access-token': token || '' },
      });
      if (response.ok) {
        const data = await response.json();
        setFlatAffectedChannels(data);
      }
    } catch (err) {
      console.error('Failed to fetch affected channels:', err);
      setFlatAffectedChannels(null);
    } finally {
      setLoadingFlatAffectedChannels(false);
    }
  };

  const handleConfirmFlatDefault = () => {
    onConfigChange({ defaultSkipVideoFolder: pendingFlatDefault === true });
    setShowFlatConfirmDialog(false);
    setPendingFlatDefault(null);
    setShowFlatAffectedList(false);
  };

  const handleCancelFlatDefault = () => {
    setShowFlatConfirmDialog(false);
    setPendingFlatDefault(null);
    setShowFlatAffectedList(false);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    let parsedValue: any = value;

    if (name === 'channelFilesToDownload') {
      parsedValue = Number(value);
    }

    onConfigChange({ [name]: parsedValue });
  };

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    onConfigChange({ [event.target.name]: event.target.checked });
  };

  const handleChannelFilesChange = (event: SelectChangeEvent<string>) => {
    onConfigChange({ channelFilesToDownload: Number(event.target.value) });
  };

  const handleSelectChange = (
    event: ChangeEvent<{ value: unknown }>,
    name: string
  ) => {
    onConfigChange({ [name]: FREQUENCY_MAPPING[event.target.value as string] });
  };

  const currentFrequency = reverseFrequencyMapping(config.channelDownloadFrequency);

  return (
    <ConfigurationCard
      title="Core Settings"
    >
      <Grid container spacing={2} className="mt-2">
        <Grid item xs={12}>
          <Accordion defaultExpanded style={{ border: 'var(--border-weight) solid var(--border)', borderRadius: 'var(--radius-ui)' }}>
            <AccordionSummary>
              <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                General Settings
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Box className="flex items-center">
                    <FormControlLabel
                      control={
                        <Switch
                          name="channelVideosHotLoad"
                          checked={config.channelVideosHotLoad}
                          onChange={handleCheckboxChange}
                        />
                      }
                      label="Enable Hot Loading"
                    />
                    <InfoTooltip
                      text="When enabled, channel lists, channel videos, and download history use infinite hot loading. When disabled, they use page-by-page controls."
                      onMobileClick={onMobileTooltipClick}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box className="flex items-center">
                    <FormControlLabel
                      control={
                        <Switch
                          name="subtitlesEnabled"
                          checked={config.subtitlesEnabled}
                          onChange={handleCheckboxChange}
                        />
                      }
                      label="Enable Subtitle Downloads"
                    />
                    <InfoTooltip
                      text="Download subtitles in SRT format when available. Manual subtitles are preferred, with auto-generated subtitles as fallback."
                      onMobileClick={onMobileTooltipClick}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box className="flex items-center">
                    <FormControlLabel
                      control={
                        <Switch
                          name="channelAutoDownload"
                          checked={config.channelAutoDownload}
                          onChange={handleCheckboxChange}
                        />
                      }
                      label="Enable Automatic Downloads"
                    />
                    <InfoTooltip
                      text="Globally enable or disable automatic scheduled downloading of videos from your channels and playlists. Only enabled channel tabs and auto-download enabled playlists will be checked and downloaded."
                      onMobileClick={onMobileTooltipClick}
                    />
                  </Box>
                </Grid>

                {config.subtitlesEnabled && (
                  <Grid item xs={12} md={6}>
                    <Box className="flex items-start">
                      <SubtitleLanguageSelector
                        value={config.subtitleLanguage}
                        onChange={(value) => onConfigChange({ subtitleLanguage: value })}
                      />
                      <Box className="flex items-center min-h-[48px] mt-5">
                        <InfoTooltip
                          text="Select one or more subtitle languages. Subtitles will be downloaded when available; videos without subtitles will still download successfully."
                          onMobileClick={onMobileTooltipClick}
                        />
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid item xs={12}>
          <Accordion defaultExpanded style={{ border: 'var(--border-weight) solid var(--border)', borderRadius: 'var(--radius-ui)' }}>
            <AccordionSummary>
              <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                Download Settings
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Download Frequency</InputLabel>
                    <Box className="flex items-center gap-1">
                      <Select
                        value={currentFrequency}
                        onChange={(e: SelectChangeEvent<string>) =>
                          handleSelectChange(e as any, 'channelDownloadFrequency')
                        }
                        label="Download Frequency"
                        disabled={!config.channelAutoDownload}
                        className="flex-1 min-w-0"
                      >
                        {Object.keys(FREQUENCY_MAPPING).map((key) => (
                          <MenuItem key={key} value={key}>
                            {key}
                          </MenuItem>
                        ))}
                      </Select>
                      <InfoTooltip
                        text="How often to run automatic channel video downloads."
                        onMobileClick={onMobileTooltipClick}
                      />
                    </Box>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Files to Download per Channel/Playlist</InputLabel>
                    <Box className="flex items-center gap-1">
                      <Select
                        value={config.channelFilesToDownload}
                        onChange={handleChannelFilesChange}
                        label="Videos to Download per Channel Tab"
                        className="flex-1 min-w-0"
                      >
                        {getChannelFilesOptions(config.channelFilesToDownload).map(count => (
                          <MenuItem key={count} value={count}>
                            {count} {count === 1 ? 'video' : 'videos'}
                          </MenuItem>
                        ))}
                      </Select>
                      <InfoTooltip
                        text="How many videos Youtarr will attempt to download per channel tab and per playlist when downloads run (channels: newest uploads; playlists: most recently added). Already downloaded videos will be skipped."
                        onMobileClick={onMobileTooltipClick}
                      />
                    </Box>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Preferred Resolution</InputLabel>
                    <Box className="flex items-center gap-1">
                      <Select
                        value={config.preferredResolution}
                        onChange={(e: SelectChangeEvent<string>) =>
                          onConfigChange({ preferredResolution: e.target.value })
                        }
                        label="Preferred Resolution"
                        className="flex-1 min-w-0"
                      >
                        <MenuItem value="2160">4K (2160p)</MenuItem>
                        <MenuItem value="1440">1440p</MenuItem>
                        <MenuItem value="1080">1080p</MenuItem>
                        <MenuItem value="720">720p</MenuItem>
                        <MenuItem value="480">480p</MenuItem>
                        <MenuItem value="360">360p</MenuItem>
                      </Select>
                      <InfoTooltip
                        text="The resolution we will try to download from YouTube. Note that this is not guaranteed as YouTube may not have your preferred resolution available. YouTube only provides H.264 MP4 up to 1080p. Selecting 1440p or 2160p (4K) will use VP9 or AV1 (remuxed into MP4), which older Plex clients (Apple TV HD, iOS, older Rokus) may need to transcode."
                        onMobileClick={onMobileTooltipClick}
                      />
                    </Box>
                    {(config.preferredResolution === '1440' || config.preferredResolution === '2160') && (
                      <Box component="span" className="text-xs text-muted-foreground">
                        1440p+ uses VP9/AV1 (remuxed into MP4). Older Plex clients without native VP9/AV1 decode may transcode. Select H.264 codec below for best compatibility (caps at 1080p).
                      </Box>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Preferred Video Codec</InputLabel>
                    <Box className="flex items-center gap-1">
                      <Select
                        value={config.videoCodec}
                        onChange={(e: SelectChangeEvent<string>) =>
                          onConfigChange({ videoCodec: e.target.value })
                        }
                        label="Preferred Video Codec"
                        className="flex-1 min-w-0"
                      >
                        <MenuItem value="default">Default (No Preference)</MenuItem>
                        <MenuItem value="h264">H.264/AVC (Best Compatibility)</MenuItem>
                        <MenuItem value="h265">H.265/HEVC (Balanced)</MenuItem>
                      </Select>
                      <InfoTooltip
                        text="Select your preferred video codec. Youtarr will download this codec when available, and fall back if it is not. H.264 is recommended for Apple TV and maximum device compatibility, but YouTube does not provide H.264 above 1080p so selecting it effectively caps downloads at 1080p regardless of the resolution preference above. Default lets YouTube pick the best codec (typically VP9 or AV1 at 1440p+)."
                        onMobileClick={onMobileTooltipClick}
                      />
                    </Box>
                    <Box component="span" className="text-xs text-muted-foreground">
                      Note: H.264 offers maximum compatibility (Apple TV HD, iOS, older Rokus direct-play) but YouTube caps H.264 at 1080p, so it will override any 1440p/2160p preference.
                    </Box>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Accordion style={{ border: 'var(--border-weight) solid var(--border)', borderRadius: 'var(--radius-ui)' }}>
                    <AccordionSummary>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        Jellyfin / Kodi / Emby Setting Information
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" style={{ marginBottom: 8 }}>
                        Control generation of metadata and artwork files that help Kodi, Emby and Jellyfin index your downloads cleanly.
                      </Typography>
                      <Typography variant="body2" style={{ fontWeight: 500, marginBottom: 8 }}>
                        For best results:
                      </Typography>
                      <Typography variant="body2">
                        • Add your download library as Content Type: <strong>Movies</strong>
                        <br />
                        • Under Metadata Readers/Savers, select <strong>Nfo</strong> to read the .nfo files
                        <br />
                        • Uncheck all metadata downloaders since we provide metadata via .nfo files
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Grid>

                <Grid item xs={12} md={6} className="mt-3">
                  <FormControl>
                    <FormControlLabel
                      control={
                        <Switch
                          name="writeVideoNfoFiles"
                          checked={config.writeVideoNfoFiles}
                          onChange={handleCheckboxChange}
                        />
                      }
                      label={
                        <Box className="flex items-center">
                          Generate video .nfo files
                          <InfoTooltip
                            text="Create .nfo metadata alongside each download so Kodi, Emby and Jellyfin can import videos with full details."
                            onMobileClick={onMobileTooltipClick}
                          />
                        </Box>
                      }
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6} className="mt-3">
                  <FormControl>
                    <FormControlLabel
                      control={
                        <Switch
                          name="writeChannelPosters"
                          checked={config.writeChannelPosters}
                          onChange={handleCheckboxChange}
                        />
                      }
                      label={
                        <Box className="flex items-center">
                          Copy channel poster.jpg files
                          <InfoTooltip
                            text="Copy channel thumbnails into each channel folder as poster.jpg for media server compatibility."
                            onMobileClick={onMobileTooltipClick}
                          />
                        </Box>
                      }
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6} className="mt-3">
                  <FormControl>
                    <FormControlLabel
                      control={
                        <Switch
                          name="writeVideoFanart"
                          checked={config.writeVideoFanart}
                          onChange={handleCheckboxChange}
                        />
                      }
                      label={
                        <Box className="flex items-center">
                          Create video fanart files
                          <InfoTooltip
                            text="Create -fanart.jpg files for each video with the video thumbnail. Some Plex clients like NVIDIA Shield use this as the background preview instead of the poster."
                            onMobileClick={onMobileTooltipClick}
                          />
                        </Box>
                      }
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid item xs={12}>
          <Accordion defaultExpanded style={{ border: 'var(--border-weight) solid var(--border)', borderRadius: 'var(--radius-ui)' }}>
            <AccordionSummary>
              <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                File Structure Settings
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel htmlFor="youtubeOutputDirectory" className="flex items-center gap-2">
                      YouTube Output Directory
                      <Chip label="Docker Volume" size="small" />
                    </InputLabel>
                    <TextField
                      id="youtubeOutputDirectory"
                      fullWidth
                      name="youtubeOutputDirectory"
                      value={config.youtubeOutputDirectory}
                      onChange={handleInputChange}
                      disabled={true}
                      helperText={
                        deploymentEnvironment.platform?.toLowerCase() === "elfhosted"
                          ? "This path is configured by your platform deployment and cannot be changed here."
                          : "Configured via YOUTUBE_OUTPUT_DIR environment variable. Edit .env and restart to change."
                      }
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box className="flex items-start">
                    <SubfolderAutocomplete
                      mode="global"
                      value={config.defaultSubfolder || null}
                      onChange={handleDefaultSubfolderChange}
                      subfolders={subfolders}
                      loading={subfoldersLoading}
                      createSubfolder={createSubfolder}
                      label="Default Subfolder"
                      helperText="Default download location for channels using 'Default Subfolder'"
                    />
                    <Box className="flex items-center min-h-[48px] mt-5">
                      <InfoTooltip
                        text="Set the default download location for untracked channels and channels using 'Default Subfolder'. Leave empty to download to the root directory by default."
                        onMobileClick={onMobileTooltipClick}
                      />
                    </Box>
                  </Box>
                  <Button variant="text" size="sm" onClick={() => setManageOpen(true)}>
                    Manage Subfolders
                  </Button>
                  <ManageSubfoldersDialog
                    open={manageOpen}
                    onClose={() => setManageOpen(false)}
                    token={token}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box className="flex items-center md:mt-5 md:min-h-[48px]">
                    <FormControlLabel
                      control={
                        <Switch
                          name="useTmpForDownloads"
                          checked={config.useTmpForDownloads}
                          onChange={handleCheckboxChange}
                          disabled={isPlatformManaged.useTmpForDownloads}
                        />
                      }
                      label={
                        <Box className="flex items-center gap-2">
                          Use external temp directory
                          {isPlatformManaged.useTmpForDownloads && (
                            <Chip
                              label={deploymentEnvironment.platform?.toLowerCase() === "elfhosted" ? "Managed by Elfhosted" : "Platform Managed"}
                              size="small"
                            />
                          )}
                        </Box>
                      }
                    />
                    <InfoTooltip
                      text={
                        isPlatformManaged.useTmpForDownloads
                          ? 'This setting is managed by your platform deployment and cannot be changed.'
                          : 'Controls where downloads are staged before moving to final location. When enabled, uses external /tmp path (useful for slow network storage). When disabled, uses a hidden .youtarr_tmp/ folder in your output directory (faster for local/SSD storage). Both options hide in-progress files from media servers.'
                      }
                      onMobileClick={onMobileTooltipClick}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box className="flex items-center">
                    <FormControlLabel
                      control={
                        <Switch
                          name="defaultSkipVideoFolder"
                          checked={config.defaultSkipVideoFolder}
                          onChange={handleFlatDefaultChange}
                        />
                      }
                      label="Flat file structure by default"
                    />
                    <InfoTooltip
                      text="When enabled, new downloads are saved directly in each channel folder instead of individual per-video subfolders. Channels can override this in their own settings (Flat or Video subfolders). Only affects new downloads; existing files are not moved."
                      onMobileClick={onMobileTooltipClick}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box className="border-t pt-3">
                    <VideoFilenameTemplate
                      value={config.videoFilenamePrefix}
                      onChange={(newValue) => onConfigChange({ videoFilenamePrefix: newValue })}
                      token={token}
                      saveRequirement={filenameTemplateSaveRequirement}
                      onPreviewSuccess={onFilenameTemplatePreviewSuccess}
                    />
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>

      {/* yt-dlp Version Section */}
      {ytDlpVersionInfo && ytDlpVersionInfo.currentVersion && (
        <>
          <Divider className="mt-6 mb-4" />
          <Box>
            <Box className="flex items-center gap-3 flex-wrap mb-2">
              <Typography variant="subtitle1" className="font-medium">
                yt-dlp:
              </Typography>
              <Typography
                variant="body1"
                style={{ fontFamily: 'monospace', fontWeight: 500 }}
              >
                {ytDlpVersionInfo.currentVersion}
              </Typography>
              {!isPlatformManaged.ytdlpUpdates && ytDlpVersionInfo.updateAvailable && ytDlpVersionInfo.latestVersion ? (
                <>
                  <ArrowForwardIcon style={{ fontSize: 16 }} className="text-muted-foreground" />
                  <Typography
                    variant="body1"
                    style={{ fontFamily: 'monospace', fontWeight: 500, color: 'var(--warning)' }}
                  >
                    {ytDlpVersionInfo.latestVersion}
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    color="warning"
                    startIcon={
                      ytDlpUpdateStatus === 'updating' ? (
                        <CircularProgress size={16} />
                      ) : (
                        <SystemUpdateIcon />
                      )
                    }
                    onClick={() => setShowYtDlpUpdateDialog(true)}
                    disabled={ytDlpUpdateStatus === 'updating'}
                  >
                    {ytDlpUpdateStatus === 'updating' ? 'Updating...' : 'Update'}
                  </Button>
                </>
              ) : !isPlatformManaged.ytdlpUpdates ? (
                <CheckCircleIcon color="success" fontSize="small" />
              ) : null}
              {isPlatformManaged.ytdlpUpdates && (
                <Chip
                  label={deploymentEnvironment.platform?.toLowerCase() === 'elfhosted' ? 'Managed by Elfhosted' : 'Platform Managed'}
                  size="small"
                />
              )}
            </Box>
            {isPlatformManaged.ytdlpUpdates ? (
              <Typography variant="caption" color="text.secondary">
                yt-dlp is managed by {deploymentEnvironment.platform?.toLowerCase() === 'elfhosted' ? 'Elfhosted' : 'the platform'} and cannot be updated from Youtarr. Updates are applied automatically by the platform.
              </Typography>
            ) : (
              <>
                <Typography variant="caption" color="text.secondary">
                  yt-dlp is the video download engine. If downloads are failing, try updating yt-dlp to the latest version.
                </Typography>

                <Box className="mt-4 flex items-center">
                  <FormControlLabel
                    control={
                      <Switch
                        name="autoUpdateYtdlp"
                        checked={!!config.autoUpdateYtdlp}
                        onChange={handleCheckboxChange}
                      />
                    }
                    label="Automatically update yt-dlp nightly"
                  />
                  <InfoTooltip
                    text="Checks for a new yt-dlp release each night at 4:00 AM (server local time) and installs it automatically. Updates are skipped while a download is in progress and will be retried the following night. If an update fails, Youtarr keeps running on the previous version."
                    onMobileClick={onMobileTooltipClick}
                  />
                </Box>

                {(config.ytdlpLastChecked || config.ytdlpLastResult || config.ytdlpLastUpdated) && (
                  <Box className="mt-1">
                    {config.ytdlpLastChecked && (
                      <Typography
                        variant="caption"
                        className="block"
                        style={{ color: config.ytdlpLastResult?.status === 'error' ? 'var(--warning)' : undefined }}
                        color={config.ytdlpLastResult?.status === 'error' ? undefined : 'text.secondary'}
                      >
                        Last checked: {formatDateTime(config.ytdlpLastChecked)}
                        {config.ytdlpLastResult?.status === 'up-to-date' && ' — already up to date'}
                        {config.ytdlpLastResult?.status === 'updated' && config.ytdlpLastResult.version && ` — updated to ${config.ytdlpLastResult.version}`}
                        {config.ytdlpLastResult?.status === 'skipped' && ` — skipped: ${config.ytdlpLastResult.message || 'reason unknown'}`}
                        {config.ytdlpLastResult?.status === 'error' && ` — update failed: ${config.ytdlpLastResult.message || 'reason unknown'}`}
                      </Typography>
                    )}
                    {config.ytdlpLastUpdated && (
                      <Typography variant="caption" color="text.secondary" className="block">
                        Last updated: {formatDateTime(config.ytdlpLastUpdated)}
                      </Typography>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>
        </>
      )}

      {/* yt-dlp Update Confirmation Dialog */}
      <Dialog
        open={showYtDlpUpdateDialog}
        onClose={() => setShowYtDlpUpdateDialog(false)}
        aria-labelledby="ytdlp-update-dialog-title"
        aria-describedby="ytdlp-update-dialog-description"
      >
        <DialogTitle id="ytdlp-update-dialog-title">Update yt-dlp?</DialogTitle>
        <DialogContent>
          <DialogContentText id="ytdlp-update-dialog-description">
            This will update yt-dlp from{' '}
            <strong>{ytDlpVersionInfo?.currentVersion || 'current version'}</strong> to{' '}
            <strong>{ytDlpVersionInfo?.latestVersion || 'latest version'}</strong>.
          </DialogContentText>
          <DialogContentText className="mt-4">
            Newer versions are not guaranteed to be fully compatible with Youtarr. Updating is only recommended if you are experiencing issues with downloading videos.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowYtDlpUpdateDialog(false)}>Cancel</Button>
          <Button
            onClick={() => {
              setShowYtDlpUpdateDialog(false);
              onYtDlpUpdate?.();
            }}
            variant="contained"
            color="primary"
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for Default Subfolder */}
      <Dialog open={showConfirmDialog} onClose={handleCancelDefaultSubfolder}>
        <DialogTitle>Set Default Subfolder?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Setting a default subfolder will affect where videos are downloaded for:
          </DialogContentText>
          <Box component="ul" className="mt-2 pl-4">
            <li>Untracked channels (manual URL downloads)</li>
            <li>Channels configured to use &quot;Default Subfolder&quot;</li>
          </Box>

          {/* Affected channels section */}
          <Box className="mt-4 mb-4">
            {loadingAffectedChannels ? (
              <Box className="flex items-center gap-2">
                <CircularProgress size={16} />
                  <span>Checking affected channels...</span>
              </Box>
            ) : affectedChannels.count === 0 ? (
              <DialogContentText>
                No tracked channels are currently using Default Subfolder.
              </DialogContentText>
            ) : (
              <>
                <DialogContentText>
                  {affectedChannels.count} tracked channel{affectedChannels.count !== 1 ? 's' : ''} configured to use Default Subfolder.
                </DialogContentText>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => setShowAffectedList(!showAffectedList)}
                  className="mt-1 block cursor-pointer"
                >
                  {showAffectedList ? 'Hide affected channels ▲' : 'Show affected channels ▼'}
                </Link>
                <Collapse in={showAffectedList}>
                  <Box
                    component="ul"
                    className="mt-2 pl-4 max-h-[200px] overflow-auto bg-muted/50 rounded py-2"
                  >
                    {affectedChannels.channelNames.map((name, index) => (
                      <li key={index}>{name}</li>
                    ))}
                  </Box>
                </Collapse>
              </>
            )}
          </Box>

          <DialogContentText>
            Videos will be downloaded to channel folders in:{' '}
            <strong>
              {pendingDefaultSubfolder ? `__${pendingDefaultSubfolder}` : 'the root directory'}
            </strong>
          </DialogContentText>
          <DialogContentText className="mt-2" style={{ fontStyle: 'italic' }}>
            Existing videos will not be moved. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDefaultSubfolder}>Cancel</Button>
          <Button onClick={handleConfirmDefaultSubfolder} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showFlatConfirmDialog} onClose={handleCancelFlatDefault}>
        <DialogTitle>Change default file structure?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingFlatDefault
              ? 'New downloads for channels using the global setting will be saved directly in the channel folder (flat structure, no per-video subfolders).'
              : 'New downloads for channels using the global setting will be saved in individual per-video subfolders.'}
          </DialogContentText>

          <Box className="mt-4 mb-4">
            {loadingFlatAffectedChannels ? (
              <Box className="flex items-center gap-2">
                <CircularProgress size={16} />
                <span>Checking affected channels...</span>
              </Box>
            ) : flatAffectedChannels === null ? (
              <DialogContentText style={{ color: 'var(--warning)' }}>
                Could not determine how many channels are affected. You can still continue, but the
                affected channel count is unknown.
              </DialogContentText>
            ) : flatAffectedChannels.count === 0 ? (
              <DialogContentText>
                No tracked channels are currently using the global setting.
              </DialogContentText>
            ) : (
              <>
                <DialogContentText>
                  {flatAffectedChannels.count} tracked channel{flatAffectedChannels.count !== 1 ? 's' : ''} follow{flatAffectedChannels.count === 1 ? 's' : ''} the global setting and will be affected.
                </DialogContentText>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => setShowFlatAffectedList(!showFlatAffectedList)}
                  className="mt-1 block cursor-pointer"
                >
                  {showFlatAffectedList ? 'Hide affected channels ▲' : 'Show affected channels ▼'}
                </Link>
                <Collapse in={showFlatAffectedList}>
                  <Box
                    component="ul"
                    className="mt-2 pl-4 max-h-[200px] overflow-auto bg-muted/50 rounded py-2"
                  >
                    {flatAffectedChannels.channelNames.map((name, index) => (
                      <li key={index}>{name}</li>
                    ))}
                  </Box>
                </Collapse>
              </>
            )}
          </Box>

          <DialogContentText>
            Previously downloaded videos are not affected. Existing files will not be moved or renamed; only new downloads use the new structure.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelFlatDefault}>Cancel</Button>
          <Button onClick={handleConfirmFlatDefault} variant="contained" color="primary" disabled={loadingFlatAffectedChannels}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </ConfigurationCard>
  );
};
